import React, { useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { tenantAPI, platformAdminAPI } from '../../services/api';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import AdminLogin, { ADMIN_TOKEN_KEY, ADMIN_USER_KEY } from './AdminLogin';
import AdminDashboard from './AdminDashboard';
import TenantsTable from './TenantsTable';
import TenantDetailsDialog from './TenantDetailsDialog';
import DeleteTenantDialog from './DeleteTenantDialog';
import ProvisionDialog from './ProvisionDialog';
import CredentialsDialog from './CredentialsDialog';
import ToggleStatusDialog from './ToggleStatusDialog';
import TenantSchoolPaySettingsDrawer from './TenantSchoolPaySettingsDrawer';

export const IMPERSONATION_KEY = 'impersonation_session'; // kept for Layout.js reference

const CentralAdminPanel = () => {
  const [admin, setAdmin] = useState(() => {
    const stored = localStorage.getItem(ADMIN_USER_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [tenants, setTenants]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [snackbar, setSnackbar]         = useState({ open: false, message: '', severity: 'success' });
  const [impersonating, setImpersonating] = useState(null);

  // Detail dialog
  const [detailOpen, setDetailOpen]       = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Provision dialog
  const [provisionOpen, setProvisionOpen] = useState(false);

  // Toggle status dialog
  const [toggleOpen, setToggleOpen]     = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Credentials dialog
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [credentials, setCredentials]         = useState({ schoolId: '', email: '', password: '' });

  // SchoolPay settings dialog
  const [schoolPaySettingsOpen, setSchoolPaySettingsOpen] = useState(false);
  const [schoolPaySettingsTarget, setSchoolPaySettingsTarget] = useState(null);

  useEffect(() => {
    if (admin) fetchTenants();
  }, [admin]);

  const getConfig = () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const { data } = await tenantAPI.getAll(getConfig());
      setTenants(data.tenants || []);
    } catch (err) {
      if (err.response?.status === 401) { handleLogout(); return; }
      setSnackbar({ open: true, message: 'Failed to load tenants', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (id) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const { data } = await tenantAPI.getById(id, getConfig());
      setSelectedTenant(data.tenant);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load tenant details', severity: 'error' });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await tenantAPI.delete(deleteTarget.id, getConfig());
      setSnackbar({ open: true, message: `Tenant "${deleteTarget.id}" deleted`, severity: 'success' });
      setDeleteOpen(false);
      setDeleteTarget(null);
      fetchTenants();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to delete', severity: 'error' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleStatus = async (reason) => {
    setToggleLoading(true);
    try {
      const { data } = await tenantAPI.toggleStatus(toggleTarget.id, { reason }, getConfig());
      setSnackbar({ open: true, message: data.message, severity: 'success' });
      setToggleOpen(false);
      setToggleTarget(null);
      fetchTenants();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update status', severity: 'error' });
    } finally {
      setToggleLoading(false);
    }
  };

  const handleProvisionSuccess = (creds) => {
    setProvisionOpen(false);
    setCredentials(creds);
    setCredentialsOpen(true);
    fetchTenants();
  };

  const handleCopy = (text, message) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message, severity: 'success' });
  };

  const handleEnterSchool = async (tenant) => {
    setImpersonating(tenant.id);
    try {
      const { data } = await platformAdminAPI.impersonate(tenant.id, getConfig());
      // data.token is a short-lived 60s stancl impersonation token
      // The new tab hits /api/impersonate/exchange?token=xxx&tenant=xxx to log in
      window.open(`/?impersonate=${data.token}&tenant=${data.tenant_code}&school=${encodeURIComponent(data.school_name)}`, '_blank');
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to enter school', severity: 'error' });
    } finally {
      setImpersonating(null);
    }
  };

  const handleLogout = async () => {
    try {
      await platformAdminAPI.logout(getConfig());
    } catch { /* ignore */ }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    setAdmin(null);
  };

  if (!admin) {
    return <AdminLogin onLogin={(a) => setAdmin(a)} />;
  }

  return (
    <AdminLayout admin={admin} onLogout={handleLogout}>
      <Routes>
        <Route path="dashboard" element={<AdminDashboard tenants={tenants} />} />
        <Route
          path="schools"
          element={
            <TenantsTable
              tenants={tenants}
              loading={loading}
              onRefresh={fetchTenants}
              onViewDetails={handleViewDetails}
              onDelete={(t) => { setDeleteTarget(t); setDeleteOpen(true); }}
              onToggleStatus={(t) => { setToggleTarget(t); setToggleOpen(true); }}
              onProvision={() => setProvisionOpen(true)}
              onEnterSchool={handleEnterSchool}
              onSchoolPaySettings={(t) => { setSchoolPaySettingsTarget(t); setSchoolPaySettingsOpen(true); }}
              impersonating={impersonating}
            />
          }
        />
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>

      <ToggleStatusDialog
        open={toggleOpen}
        onClose={() => setToggleOpen(false)}
        onConfirm={handleToggleStatus}
        tenant={toggleTarget}
        loading={toggleLoading}
      />

      <TenantDetailsDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        tenant={selectedTenant}
        loading={detailLoading}
        getAuthConfig={getConfig}
      />

      <DeleteTenantDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        tenant={deleteTarget}
        loading={deleteLoading}
      />

      <ProvisionDialog
        open={provisionOpen}
        onClose={() => setProvisionOpen(false)}
        onSuccess={handleProvisionSuccess}
      />

      <CredentialsDialog
        open={credentialsOpen}
        onClose={() => setCredentialsOpen(false)}
        credentials={credentials}
        onCopy={handleCopy}
      />

      <TenantSchoolPaySettingsDrawer
        open={schoolPaySettingsOpen}
        onClose={() => setSchoolPaySettingsOpen(false)}
        tenant={schoolPaySettingsTarget}
        getConfig={getConfig}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  );
};

export default CentralAdminPanel;
